import Grid from '@material-ui/core/Grid';
import GridFormField from 'shared/components/GridFormField';
import React from 'react';
import removeHTML from 'shared/utils/removeHTML';
import Typography from '@material-ui/core/Typography';
import {
	dateInRange,
	isAfterDob,
	maxStrLength,
	numericValidation,
	phoneNumberValidation,
	settingValidation,
	restrictedCompanyNameTextInputValidation,
} from 'shared/utils/validations';
import { formatNumber, formatPhone, parseNumber, parsePhone } from 'shared/utils/formatting';
import { FormSection } from 'redux-form';
import { OPTIONAL, SKIP } from 'redux/domain/Settings/constants';
import { PropTypes } from 'prop-types';
import {
	CURRENT_EMPLOYMENT,
	DATE_RANGE_START_YEAR,
	DATE_RANGE_START_MONTH,
	DATE_RANGE_START_DAY,
	INCOME_TYPE_OPTIONS,
} from './constants';

class CurrentEmploymentFields extends React.Component {
	constructor() {
		super();
		this.employmentFieldSettings = this.employmentFieldSettings.bind(this);
		this.employmentFieldValidation = this.employmentFieldValidation.bind(this);
	}

	employmentFieldSettings(fieldSettings, formValues = this.props.formValues) {
		const employmentStatusValue =
			formValues[CURRENT_EMPLOYMENT] && formValues[CURRENT_EMPLOYMENT].employmentStatus;
		const isEmploymentInfoRequired =
			employmentStatusValue &&
			this.props.settings.comboFieldsByValue.EMPSTATUS[employmentStatusValue]
				.requiresEmploymentData;
		if (fieldSettings.fieldStatus === SKIP || isEmploymentInfoRequired) {
			return fieldSettings;
		}
		return { ...fieldSettings, fieldStatus: OPTIONAL };
	}

	employmentFieldValidation(fieldSettings) {
		return (value, formValues) => {
			const empFieldSettings = this.employmentFieldSettings(fieldSettings, formValues);
			return settingValidation(empFieldSettings)(value);
		};
	}

	render() {
		const { disableAll, settings, name } = this.props;
		const empStatusOptions = settings.comboFields.EMPSTATUS;
		return (
			<FormSection name={name} style={{ width: '100%' }}>
				<Grid container spacing={5}>
					<GridFormField
						fieldSettings={settings.primary.employmentStatus}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.EmploymentStatusLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.EmploymentStatusDescription}
						menuOptions={empStatusOptions}
						select
						name="employmentStatus"
						validation={settingValidation(settings.primary.employmentStatus)}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.employmentFieldSettings(settings.primary.employer)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.EmployerLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.EmployerDescription}
						name="name"
						validation={[
							this.employmentFieldValidation(settings.primary.employer),
							maxStrLength(30),
							restrictedCompanyNameTextInputValidation,
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.employmentFieldSettings(settings.primary.jobTitle)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.JobTitleLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.JobTitleDescription}
						name="jobTitle"
						validation={[
							this.employmentFieldValidation(settings.primary.jobTitle),
							maxStrLength(30),
						]}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.employmentFieldSettings(settings.primary.employerPhone)}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.EmployerPhoneLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.EmployerPhoneDescription}
						name="phone"
						validation={[
							this.employmentFieldValidation(settings.primary.employerPhone),
							phoneNumberValidation('US'),
						]}
						format={formatPhone('US')}
						parse={parsePhone('US')}
						disabled={disableAll}
					/>
					<GridFormField
						fieldSettings={this.employmentFieldSettings(settings.primary.timeAtEmployer)}
						validation={[
							this.employmentFieldValidation(settings.primary.timeAtEmployer),
							dateInRange(
								new Date(DATE_RANGE_START_YEAR, DATE_RANGE_START_MONTH, DATE_RANGE_START_DAY),
								new Date(Date.now()),
							),
							isAfterDob,
						]}
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.EmploymentStartLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.EmploymentStartDescription}
						type="date"
						inputProps={{ 
							style: { textTransform: 'uppercase', marginTop: '16px' },
							 placeholder: 'MM/dd/yyyy' }}
						labelShrink
						name="employmentTermStartDate"
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.IncomeLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.IncomeDescription}
						fieldSettings={this.employmentFieldSettings(settings.primary.income)}
						name="income"
						validation={[
							this.employmentFieldValidation(settings.primary.income),
							maxStrLength(6),
							numericValidation,
						]}
						format={formatNumber}
						parse={parseNumber}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.IncomeTypeLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.IncomeTypeDescription}
						fieldSettings={this.employmentFieldSettings(settings.primary.incomeIndicator)}
						select
						menuOptions={INCOME_TYPE_OPTIONS}
						name="incomeIndicator"
						validation={this.employmentFieldValidation(settings.primary.incomeIndicator)}
						disabled={disableAll}
					/>
					<GridFormField
						placeholder={settings.fieldAndFooterSettings.ApplicationPageSettings.NetIncomeLabel}
						description={settings.fieldAndFooterSettings.ApplicationPageSettings.NetIncomeDescription}
						fieldSettings={this.employmentFieldSettings(settings.primary.netIncome)}
						name="netIncome"
						validation={[
							this.employmentFieldValidation(settings.primary.netIncome),
							maxStrLength(6),
							numericValidation,
						]}
						format={formatNumber}
						parse={parseNumber}
						disabled={disableAll}
					/>
				</Grid>
				<Typography type="caption" component="div" style={{ paddingTop: '20px' }}>
					<div
						// eslint-disable-next-line react/no-danger
						dangerouslySetInnerHTML={{
							__html: removeHTML(settings.fieldAndFooterSettings.ApplicationPageSettings.CurrentEmploymentFooter),
						}}
					/>
				</Typography>
			</FormSection>
		);
	}
}

CurrentEmploymentFields.propTypes = {
	disableAll: PropTypes.bool,
	formValues: PropTypes.shape({}),
	settings: PropTypes.shape({
		primary: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.shape({
					fieldId: PropTypes.string,
					fieldStatus: PropTypes.string,
					fieldSubHeaderType: PropTypes.string,
				}),
				PropTypes.number,
			]),
		),
		comboFields: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.arrayOf(
					PropTypes.shape({ displayText: PropTypes.string, value: PropTypes.string }),
				),
				PropTypes.number,
			]),
		),
		comboFieldsByValue: PropTypes.objectOf(
			PropTypes.oneOfType([
				PropTypes.objectOf(
					PropTypes.shape({
						displayOrder: PropTypes.number,
						fieldName: PropTypes.string,
						displayText: PropTypes.string,
						returnValue: PropTypes.string,
						requiresEmploymentData: PropTypes.bool,
					}),
				),
				PropTypes.number,
			]),
		),
	}),
	name: PropTypes.string,
};

CurrentEmploymentFields.defaultProps = {
	disableAll: false,
	formValues: {},
	settings: {},
	name: CURRENT_EMPLOYMENT,
};

export default CurrentEmploymentFields;
